import React, { useState, useContext, useEffect } from "react";
import {
  Card,
  Table,
  Row,
  Col,
  Typography,
  Select,
  Progress,
  Tabs,
  Tag,
  Divider,
  Statistic,
  Spin,
} from "antd";
import { Pie, Bar } from "react-chartjs-2";
import { AppContext } from "../context/AppContext";
import {
  WarningOutlined,
  BankOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import axios from "axios";
import client from "../helpers/directusClient";
import { readItems } from "@directus/sdk";

const { Title, Text } = Typography;
const { Option } = Select;

const RiskConcentrationReport = () => {
  const { company } = useContext(AppContext);
  const [filters, setFilters] = useState({
    dateRange: "2024-Q2",
    loanProduct: "all",
  });
  const [loading, setLoading] = useState(true);
  const [industryData, setIndustryData] = useState([]);
  const [geographicData, setGeographicData] = useState([]);
  const [borrowerConcentration, setBorrowerConcentration] = useState([]);
  const [totalExposure, setTotalExposure] = useState(0);
  const [highestExposure, setHighestExposure] = useState(0);
  const [topSectorConcentration, setTopSectorConcentration] = useState({ sector: "", percentage: 0 });
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch only active loans (loan_status = 7)
        const response = await axios.get(
          `https://zedlenders.pickmesms.com/items/loans?fields=*,borrower.id,borrower.first_name,borrower.last_name,borrower.industry,borrower.location&filter[company]=${company?.id}&filter[loan_status]=7`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        
        const loans = response.data.data;
        
        // Calculate total exposure
        const total = loans.reduce((sum, loan) => sum + (loan.amount || 0), 0);
        setTotalExposure(total);
        
        // Process industry data
        const industries = {};
        loans.forEach(loan => {
          const industry = (loan.borrower?.industry || "Unspecified");
          if (!industries[industry]) {
            industries[industry] = { sector: industry, loans: 0, exposure: 0 };
          }
          industries[industry].loans += 1;
          industries[industry].exposure += (loan.amount || 0);
        });
        
        const industryArray = Object.values(industries);
        setIndustryData(industryArray);
        
        // Find top sector concentration
        if (industryArray.length > 0) {
          const topSector = industryArray.reduce((max, curr) => 
            curr.exposure > max.exposure ? curr : max, industryArray[0]);
          setTopSectorConcentration({
            sector: topSector.sector,
            percentage: Math.round((topSector.exposure / total) * 100)
          });
        }
        
        // Process geographic data
        const regions = {};
        loans.forEach(loan => {
          const region = (loan.borrower?.location || "Unspecified");
          if (!regions[region]) {
            regions[region] = { region, loans: 0, exposure: 0 };
          }
          regions[region].loans += 1;
          regions[region].exposure += (loan.amount || 0);
        });
        
        setGeographicData(Object.values(regions));
        
        // Process borrower concentration
        const borrowers = {};
        loans.forEach(loan => {
          if (loan.borrower) {
            const borrowerId = loan.borrower.id;
            const borrowerName = `${loan.borrower.first_name} ${loan.borrower.last_name}`;
            
            if (!borrowers[borrowerId]) {
              borrowers[borrowerId] = { 
                id: borrowerId,
                name: borrowerName, 
                exposure: 0, 
                percentage: 0 
              };
            }
            borrowers[borrowerId].exposure += (loan.amount || 0);
          }
        });
        
        // Calculate percentages and sort borrowers
        const borrowerArray = Object.values(borrowers).map(b => ({
          ...b,
          percentage: Math.round((b.exposure / total) * 100)
        })).sort((a, b) => b.exposure - a.exposure);
        
        // Take top 3 borrowers + calculate top 10
        const top3 = borrowerArray.slice(0, 3);
        const top10Exposure = borrowerArray.slice(0, 10).reduce((sum, b) => sum + b.exposure, 0);
        const top10Percentage = Math.round((top10Exposure / total) * 100);
        
        setBorrowerConcentration([
          ...top3,
          { name: "Top 10 Borrowers", exposure: top10Exposure, percentage: top10Percentage }
        ]);
        
        // Set highest single exposure
        if (borrowerArray.length > 0) {
          setHighestExposure(borrowerArray[0].exposure);
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching risk concentration data:", error);
        setLoading(false);
      }
    };
    
    if (company?.id) {
      fetchData();
    }
  }, [company?.id, filters.dateRange]);

  const industryChartData = {
    labels: industryData.map((i) => i.sector),
    datasets: [
      {
        data: industryData.map((i) => i.exposure),
        backgroundColor: ["#36A2EB", "#4BC0C0", "#FF6384", "#FF9F40"],
        borderWidth: 1,
      },
    ],
  };

  const geographicChartData = {
    labels: geographicData.map((g) => g.region),
    datasets: [
      {
        label: "Exposure (ZMW)",
        data: geographicData.map((g) => g.exposure),
        backgroundColor: "#1890ff77",
        borderColor: "#1890ff",
        borderWidth: 2,
      },
    ],
  };

  const columns = [
    {
      title: "Sector/Region",
      dataIndex: "name",
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Text type="secondary">{record.loans} loans</Text>
        </div>
      ),
    },
    {
      title: "Exposure",
      dataIndex: "exposure",
      render: (value) => `ZMW ${value.toLocaleString()}`,
      sorter: (a, b) => a.exposure - b.exposure,
    },
    {
      title: "Concentration",
      dataIndex: "percentage",
      render: (value) => (
        <Progress
          percent={value}
          status={value > 25 ? "exception" : value > 15 ? "active" : "normal"}
          strokeColor={value > 25 ? "#cf1322" : "#1890ff"}
        />
      ),
    },
    {
      title: "Risk Level",
      render: (_, record) => (
        <Tag
          color={
            record.percentage > 25
              ? "red"
              : record.percentage > 15
              ? "orange"
              : "green"
          }
        >
          {record.percentage > 25
            ? "High"
            : record.percentage > 15
            ? "Medium"
            : "Low"}
        </Tag>
      ),
    },
  ];

  // Calculate borrower recommendations
  const borrowerRecommendations = () => {
    const topBorrowers = borrowerConcentration.filter(
      (b) => !b.name.includes("Top")
    );
    
    if (!topBorrowers.length) {
      return {
        main: "No significant borrower concentration",
        suggestions: [
          "Continue maintaining diverse borrower portfolio",
          "Implement borrower exposure monitoring system",
          "Develop borrower risk rating system"
        ]
      };
    }
    
    const maxBorrower = topBorrowers.reduce(
      (max, curr) => (curr.percentage > max.percentage ? curr : max),
      topBorrowers[0]
    );

    return {
      main: `Address ${maxBorrower.name} concentration (${maxBorrower.percentage}% exposure)`,
      suggestions: [
        `Limit single borrower exposure to <10% (current max: ${maxBorrower.percentage}%)`,
        "Implement borrower exposure caps",
        "Enhance credit checks for concentrated borrowers",
      ],
    };
  };

  // Calculate geographic recommendations
  const geographicRecommendations = () => {
    if (!geographicData.length) {
      return {
        main: "No geographic data available",
        suggestions: ["Start collecting location data for borrowers"],
      };
    }
    
    const maxRegion = geographicData.reduce(
      (max, curr) => (curr.exposure > max.exposure ? curr : max),
      geographicData[0]
    );
    
    const exposureThreshold = totalExposure * 0.1; // 10% of total exposure
    const underrepresented = geographicData.filter(
      (r) => r.exposure < exposureThreshold && r.region !== "Other" && r.region !== "Unspecified"
    );

    return {
      main: `Diversify ${maxRegion.region} concentration (${(
        (maxRegion.exposure / (totalExposure || 1)) *
        100
      ).toFixed(1)}%)`,
      suggestions: [
        ...underrepresented.map((r) => `Expand ${r.region} operations`),
        "Develop rural lending programs",
        "Monitor regional economic indicators",
      ],
    };
  };

  // Calculate sector recommendations
  const sectorRecommendations = () => {
    if (!industryData.length) {
      return {
        main: "No industry data available",
        suggestions: ["Start collecting industry data for borrowers"],
      };
    }
    
    const maxSector = industryData.reduce(
      (max, curr) => (curr.exposure > max.exposure ? curr : max),
      industryData[0]
    );
    
    const otherSectors = industryData.filter((s) => s.sector !== maxSector.sector && s.sector !== "Unspecified");
    const topUnderweight = otherSectors.length > 0 ? 
      otherSectors.sort((a, b) => a.exposure - b.exposure)[0] : 
      { sector: "new sectors" };

    return {
      main: `Reduce ${maxSector.sector} exposure from ${(
        (maxSector.exposure / (totalExposure || 1)) *
        100
      ).toFixed(1)}%`,
      suggestions: [
        `Gradually decrease ${maxSector.sector} allocation to <40%`,
        `Develop ${topUnderweight.sector} sector portfolio`,
        `Implement sector exposure limits (max 35% per sector)`,
      ],
    };
  };

  const sector = sectorRecommendations();
  const geo = geographicRecommendations();
  const borrower = borrowerRecommendations();

  return (
    <div style={{ padding: 24 }}>
      <Card bordered={false}>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 24 }}
        >
          <Col>
            <Title level={3} style={{ marginBottom: 0 }}>
              <WarningOutlined /> Risk Concentration Analysis
            </Title>
            <Text type="secondary">{company?.name} • Q2 2024</Text>
          </Col>
          <Col>
            <Select
              value={filters.dateRange}
              onChange={(value) => setFilters({ ...filters, dateRange: value })}
              style={{ width: 200 }}
            >
              <Option value="2024-Q2">Q2 2024</Option>
              <Option value="2024-Q1">Q1 2024</Option>
              <Option value="2023-Q4">Q4 2023</Option>
            </Select>
          </Col>
        </Row>

        {/* Key Risk Metrics */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" tip="Loading risk data..." />
          </div>
        ) : (
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Card bordered={false}>
                <Statistic
                  title="Total Exposure"
                  value={totalExposure}
                  prefix="ZMW"
                  valueStyle={{ color: "#1890ff", fontSize: 24 }}
                />
                <Text type="secondary">Across all sectors</Text>
              </Card>
            </Col>
            <Col span={6}>
              <Card bordered={false}>
                <Statistic
                  title="Highest Single Exposure"
                  value={highestExposure}
                  prefix="ZMW"
                  valueStyle={{ color: "#cf1322", fontSize: 24 }}
                />
                <Text type="secondary">
                  {borrowerConcentration.length > 0 ? 
                    `${borrowerConcentration[0]?.percentage || 0}% of total portfolio` : 
                    "0% of total portfolio"}
                </Text>
              </Card>
            </Col>
            <Col span={6}>
              <Card bordered={false}>
                <Statistic
                  title="Sector Concentration"
                  value={`${topSectorConcentration.percentage}%`}
                  valueStyle={{ color: "#ff4d4f", fontSize: 24 }}
                />
                <Text type="secondary">{topSectorConcentration.sector} sector</Text>
              </Card>
            </Col>
            <Col span={6}>
              <Card bordered={false}>
                <Statistic
                  title="Diversification Score"
                  value={industryData.length > 1 ? 
                    Math.min(100, Math.max(30, 100 - topSectorConcentration.percentage)) : 30}
                  suffix="/100"
                  valueStyle={{ 
                    color: (
                      industryData.length > 1 ?
                        (100 - topSectorConcentration.percentage) < 50 ? "#cf1322" :  // Red for low diversification
                        (100 - topSectorConcentration.percentage) < 75 ? "#fa8c16" :  // Orange for medium
                        "#52c41a"  // Green for good diversification
                      : "#cf1322"  // Default to red for low diversification when not enough data
                    ), 
                    fontSize: 24 
                  }}
                />
                <Text type="secondary" style={{
                  color: industryData.length > 1 ?
                    (topSectorConcentration.percentage > 50 ? "#cf1322" : 
                    topSectorConcentration.percentage > 35 ? "#fa8c16" : 
                    "#52c41a") : 
                    "#cf1322" 
                }}>
                  {industryData.length > 1 ? 
                    (topSectorConcentration.percentage > 50 ? "Low diversification" : 
                    topSectorConcentration.percentage > 35 ? "Moderate diversification" : 
                    "Good diversification") : 
                    "Low diversification"}
                </Text>
              </Card>
            </Col>
          </Row>
        )}

        <Tabs defaultActiveKey="1">
          <Tabs.TabPane tab="Industry Exposure" key="1">
            <Row gutter={24}>
              <Col span={12}>
                <Card title="Sector Distribution" bordered={false}>
                  <Pie
                    data={industryChartData}
                    options={{
                      plugins: {
                        tooltip: {
                          callbacks: {
                            label: (context) => {
                              const sector = industryData[context.dataIndex];
                              return `${
                                sector.sector
                              }: ZMW ${sector.exposure.toLocaleString()} (${
                                sector.loans
                              } loans)`;
                            },
                          },
                        },
                      },
                    }}
                  />
                </Card>
              </Col>
              <Col span={12}>
                <Card title="Exposure by Industry" bordered={false}>
                  <Table
                    columns={columns}
                    dataSource={industryData.map((d) => ({
                      ...d,
                      name: d.sector,
                      percentage: Math.round((d.exposure / (totalExposure || 1)) * 100),
                    }))}
                    rowKey="sector"
                    pagination={false}
                  />
                </Card>
              </Col>
            </Row>
          </Tabs.TabPane>

          <Tabs.TabPane tab="Geographic Risk" key="2">
            <Row gutter={24}>
              <Col span={12}>
                <Card title="Regional Exposure" bordered={false}>
                  <Bar
                    data={geographicChartData}
                    options={{
                      responsive: true,
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            callback: (value) =>
                              `ZMW ${value.toLocaleString()}`,
                          },
                        },
                      },
                    }}
                  />
                </Card>
              </Col>
              <Col span={12}>
                <Card title="Top Regions" bordered={false}>
                  <Table
                    columns={columns}
                    dataSource={geographicData.map((d) => ({
                      ...d,
                      name: d.region,
                      percentage: Math.round((d.exposure / (totalExposure || 1)) * 100),
                    }))}
                    rowKey="region"
                    pagination={false}
                  />
                </Card>
              </Col>
            </Row>
          </Tabs.TabPane>

          <Tabs.TabPane tab="Borrower Concentration" key="3">
            <Card bordered={false}>
              <Table
                columns={columns}
                dataSource={borrowerConcentration}
                rowKey="name"
                pagination={false}
                summary={() => (
                  <Table.Summary>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0}>
                        <Text strong>Portfolio Total</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1}>
                        <Text strong>ZMW {totalExposure.toLocaleString()}</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} />
                      <Table.Summary.Cell index={3} />
                    </Table.Summary.Row>
                  </Table.Summary>
                )}
              />
              <Divider />
              <Text type="secondary">
                <GlobalOutlined /> Concentration Risk Thresholds:
                <Tag color="green" style={{ marginLeft: 8 }}>
                  Low {"<"}15%
                </Tag>
                <Tag color="orange" style={{ marginLeft: 4 }}>
                  Medium 15-25%
                </Tag>
                <Tag color="red" style={{ marginLeft: 4 }}>
                  High {">"}25%
                </Tag>
              </Text>
            </Card>
          </Tabs.TabPane>
        </Tabs>

        <Divider />

        <Card title="Risk Mitigation Recommendations" bordered={false}>
          <Row gutter={16}>
            <Col span={8}>
              <Text strong>Sector Diversification</Text>
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">{sector.main}</Text>
                <ul style={{ marginTop: 8 }}>
                  {sector.suggestions.map((s, i) => (
                    <li key={i} style={{ marginBottom: 4 }}>
                      <Text>{s}</Text>
                    </li>
                  ))}
                </ul>
              </div>
            </Col>

            <Col span={8}>
              <Text strong>Geographic Spread</Text>
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">{geo.main}</Text>
                <ul style={{ marginTop: 8 }}>
                  {geo.suggestions.map((s, i) => (
                    <li key={i} style={{ marginBottom: 4 }}>
                      <Text>{s}</Text>
                    </li>
                  ))}
                </ul>
              </div>
            </Col>

            <Col span={8}>
              <Text strong>Borrower Distribution</Text>
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">{borrower.main}</Text>
                <ul style={{ marginTop: 8 }}>
                  {borrower.suggestions.map((s, i) => (
                    <li key={i} style={{ marginBottom: 4 }}>
                      <Text>{s}</Text>
                    </li>
                  ))}
                </ul>
              </div>
            </Col>
          </Row>
        </Card>
      </Card>
    </div>
  );
};

export default RiskConcentrationReport;
